import React, {
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useParams } from "react-router";
import { PositionedLoaderIcon } from "@coworker/reusable/Loader";
import CompletedArticleList from "./CompletedArticleList";
import RemainingArticleList from "./RemainingArticleList";
import StaticFilterRow from "./StaticFilterRow";
import useFormatter from "@coworker/app/src/hooks/useFormatter";
import { useTranslation } from "@coworker/locales";
import { useTestbuy } from "../CommonComponents/Repositories/useTestbuyService";
import { useTestBuyArticles } from "../CommonComponents/Repositories/useArticleService";
import { HistoricDetails } from "./HistoricDetails";
import { useTestbuyOptionsMenu } from "./useTestbuyOptionsMenuTestbuyService";
import { ReplaceArticlesGuide } from "./ReplaceArticlesGuide";
import { COMPLETED_STATUS } from "../constants";
import { postToTestbuyService } from "../../../hooks/API/testbuy.service";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon"; // NOSONAR
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath"; // NOSONAR
import CheckmarkIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CheckmarkIconPath"; // NOSONAR
import EllipsesHorizontalIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/EllipsesHorizontalIconPath"; // NOSONAR
import styled from "styled-components"; // NOSONAR
import { AppBarAction } from "@coworker/apprestructured/src/layout/components/AppBar/AppBar";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";

type QueryParams = { id: string };

const setCaretPosition = (element: HTMLElement, col: number) => {
  const range = document.createRange();
  const selection = window.getSelection() as Selection;

  if (element.childNodes[0]) {
    range.setStart(element.childNodes[0], col);
  }

  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  element.focus();
};

const CustomTitleInput = styled("p")`
  border: none;
  background-color: transparent;
  display: inline;
  font-size: 1rem;
  font-weight: 700;
  padding: 0;
  margin: 0;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

const MessageWrapper = styled("div")`
  margin: 16px 24px;
`;

const TestbuyCustomTitleEditInput = forwardRef(
  (
    {
      createdAt,
      customTitle,
    }: {
      createdAt: string;
      customTitle: string;
    },
    customTitleEditInputRef: React.Ref<HTMLInputElement>
  ) => {
    // There are two refs needed. One of them is just a regular ref (inputRef) to be used
    // in the restrictWhiteSpaceAndLength function. The other one is a ref that will be returned
    // to the parent component (customTitleEditInputRef). This is needed to focus the input
    // when the edit mode is activated.
    // The useImperativeHandle hook is used to return the inputRef to the parent component.
    // By default both, the inputRef and ref for the parent, can't be assigned to the same element.

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(customTitleEditInputRef, () => inputRef.current!);

    const restrictWhiteSpaceAndLength = (e: React.KeyboardEvent) => {
      if (!inputRef.current) return;

      const input = inputRef.current;
      if (
        (input.selectionStart === 0 && e.code === "Space") ||
        e.code === "Enter"
      ) {
        e.preventDefault();
      }

      if (input.innerText.length >= 18 && e.code !== "Backspace") {
        e.preventDefault();
      }
    };

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.innerText = customTitle;
      }
    }, [customTitle]);

    // The editable element is not an input as the actual <input> element doesn't allow
    // to set content dependent width. Instead, a <p> element is used with the contentEditable attribute.
    return (
      <>
        <span>{`${createdAt} - `}</span>
        <CustomTitleInput
          contentEditable
          ref={inputRef}
          onKeyDown={restrictWhiteSpaceAndLength}
        >
          {decodeURIComponent(customTitle)}
        </CustomTitleInput>
      </>
    );
  }
);

export function TestBuyDetailPage() {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { t } = useTranslation();
  const store = useMyStore();
  const { formatDateISO8601 } = useFormatter();

  const tz =
    store?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { id: testbuyId } = useParams<QueryParams>();

  const {
    data: testbuy,
    isLoading: isLoadingTestbuy,
    isFetching: isFetchingTestbuy,
    refetch,
  } = useTestbuy(testbuyId);

  const {
    data: testbuyItems,
    isLoading: isloadingTestbuyItems,
    isFetching: isFetchingTestbuyItems,
  } = useTestBuyArticles(testbuyId);

  const { getOptionsMenu, loading: popUpMenuProgress } = useTestbuyOptionsMenu({
    id: testbuyId,
  });

  const isLoading =
    isLoadingTestbuy ||
    isFetchingTestbuy ||
    isFetchingTestbuyItems ||
    isloadingTestbuyItems ||
    popUpMenuProgress;

  const createdAt = testbuy?.createdAt ?? "";

  const setTestbuyCompleted = React.useCallback(
    async (testbuyId: string) => {
      await postToTestbuyService(
        "testbuy/update_testbuy_completed",
        {
          id: testbuyId,
        },
        (data: string) => {
          if (data) refetch();
        }
      );
    },
    [refetch]
  );

  const onNameEdited = (newCustomName: string) => {
    postToTestbuyService(
      "testbuy/update_custom_name",
      {
        testbuyId,
        customName: newCustomName,
      },
      () => {}
    );
  };

  const isEditable = testbuy?.status === COMPLETED_STATUS;
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const customTitleEditInputRef = useRef<HTMLInputElement>(null);

  const [customTitle, setCustomTitle] = useState(() => {
    return testbuy?.customName;
  });

  const appBarActions: AppBarAction[] = [];

  if (isEditable && !isBeingEdited) {
    appBarActions.push({
      name: "Edit name",
      icon: <FixaSSRIcon paths={PencilIconPath} />,
      onClick: () => {
        setIsBeingEdited(true);
        if (!customTitle) {
          setCustomTitle(testbuy?.customName);
        }
        setTimeout(() => {
          setCaretPosition(
            customTitleEditInputRef.current!,
            customTitle?.length || testbuy?.customName?.length
          );
        }, 0);
      },
      position: "right" as const,
    });
  } else if (!isEditable && !isBeingEdited) {
    appBarActions.push({
      name: "Options menu",
      icon: (
        <FixaButton
          size="xsmall"
          iconOnly={true}
          type="tertiary"
          ssrIcon={EllipsesHorizontalIconPath}
          aria-label={t("testBuyDetailOptionsMenuAriaString")}
        />
      ),
      onClick: () => {
        getOptionsMenu();
      },
      position: "right" as const,
    });
  }

  if (isBeingEdited) {
    appBarActions.push({
      name: "Save",
      icon: <FixaSSRIcon paths={CheckmarkIconPath} />,
      onClick: () => {
        const customTitleEditText = customTitleEditInputRef.current?.innerText;

        onNameEdited(customTitleEditText ?? "");

        setCustomTitle(customTitleEditText ?? "");
        setIsBeingEdited(false);
      },
      position: "right" as const,
    });
  }

  // Showing date according to store tz.
  const createdLocalString = createdAt
    ? dayjs(createdAt).tz(tz).format("MMMM D, YYYY")
    : "";
  const titleText =
    testbuy?.status === "IN_PROGRESS"
      ? `${createdLocalString} ${t("testBuyString")}`
      : createdLocalString;

  const customTitleText = () => {
    return testbuy?.customName
      ? ` - ${decodeURIComponent(customTitle || testbuy?.customName)}`
      : "";
  };

  const linkbarItems = useMemo(() => {
    return [
      {
        label: t("testBuysString"),
        to: "/testbuy",
      },
    ];
  }, [t]);

  return isLoading || !testbuyId ? (
    <FullScreenPopup
      appBarConfig={{
        title: titleText,
      }}
      noPadding
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={formatDateISO8601(createdAt)}
        />
      }
    >
      <PositionedLoaderIcon />
    </FullScreenPopup>
  ) : (
    <FullScreenPopup
      appBarConfig={{
        title: isBeingEdited ? (
          <TestbuyCustomTitleEditInput
            ref={customTitleEditInputRef}
            createdAt={createdAt}
            customTitle={customTitle ?? ""}
          />
        ) : (
          titleText + customTitleText()
        ),
        actions: appBarActions,
      }}
      noPadding
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={formatDateISO8601(createdAt)}
        />
      }
    >
      <StaticFilterRow
        typeOfArticles={testbuy?.typeOfArticles}
        area={testbuy?.area}
        numberOfArticles={testbuy?.numberOfArticles}
      />
      <ReplaceArticlesGuide />
      {!isloadingTestbuyItems && !isFetchingTestbuyItems && testbuyItems && (
        <>
          {testbuy?.status === COMPLETED_STATUS ? (
            <HistoricDetails testbuy={testbuy} testbuyItems={testbuyItems} />
          ) : (
            <>
              <RemainingArticleList
                testbuy={testbuy}
                testbuyItems={testbuyItems}
                onDone={setTestbuyCompleted}
              />
              <CompletedArticleList
                testbuy={testbuy}
                testbuyItems={testbuyItems}
              />
            </>
          )}
        </>
      )}
    </FullScreenPopup>
  );
}
